import * as React from 'react'

import { observer } from 'mobx-react'

import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import FormFieldWrapper from '~/client/src/shared/components/FormFieldWrapper/FormFieldWrapper'
import { ArrowBackIcon } from '~/client/src/shared/components/icons/ArrowBackIcon'
import { ArrowForwardIcon } from '~/client/src/shared/components/icons/ArrowForwardIcon'
import { CloseIcon } from '~/client/src/shared/components/icons/CloseIcon'
import { Loader } from '~/client/src/shared/components/Loader'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogContent,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import AddEditItemDialogStore, {
  IAddEditDialogField,
} from '~/client/src/shared/stores/ui/AddEditItemDialog.store'

// localization: translated

interface IProps {
  isLoading: boolean
  errorInfo: string
  store: AddEditItemDialogStore<any, any>
  customFooterRenderer?: () => JSX.Element
  // Portal the dialog into a specific container (e.g. #app) instead of <body>.
  // Needed when a child modal or the global user card must layer above this one:
  // #app has `isolation: isolate`, so body-level portals escape its context.
  portalContainer?: React.RefObject<HTMLElement | null>
  // Defaults to a full modal. Pass 'trap-focus' when something outside this
  // dialog (a stacked modal, the global user card) must stay interactive; the
  // overlay still blocks the page behind.
  modal?: boolean | 'trap-focus'
}

const ICON_BUTTON_CLASSES =
  'tw:inline-flex tw:items-center tw:justify-center tw:bg-transparent tw:border-0 tw:p-1 tw:cursor-pointer'

const DIALOG_CLASSES = [
  'tw:!w-[760px] tw:!max-w-[90vw] tw:!max-h-[700px] tw:!p-0',
  'tw:flex tw:flex-col tw:overflow-hidden',
  'tw:[&_.hierarchy-delivery-attribute-tag]:z-0',
  'tw:[&_.site-permit-input]:!h-[32px]',
].join(' ')

@observer
export default class BaseAddEditDialogWrapper extends React.Component<IProps> {
  public static renderFields(
    fields: IAddEditDialogField[],
    avatarType: AvatarType,
  ): JSX.Element[] {
    return fields.map(field => (
      <FormFieldWrapper key={field.id} field={field} avatarType={avatarType} />
    ))
  }

  public static renderErrorInfo(errorInfo: string): JSX.Element {
    return (
      errorInfo && (
        <div className="text center large red bold tw:whitespace-pre-line">
          * {errorInfo}
        </div>
      )
    )
  }

  public render() {
    const { children, store } = this.props

    const {
      preSubmit,
      submit,
      isMultiMode,
      handleCloseConfirmModal,
      handlePreClose,
      dialogCaption,
      okActionCaption,
      canFormsBeSubmitted,
      shouldShowConfirmModal,
      confirmationMessage,
      confirmationDoneBtnTitle,
    } = store

    return (
      <Dialog
        open={true}
        modal={this.props.modal}
        onOpenChange={open => {
          if (!open) {
            handlePreClose()
          }
        }}
      >
        <DialogContent
          className={DIALOG_CLASSES}
          showCloseButton={false}
          portalContainer={this.props.portalContainer}
        >
          {this.renderLoader()}
          <ConfirmDialog
            doneTitle={confirmationDoneBtnTitle}
            cancelTitle={Localization.translator.no}
            isOpen={shouldShowConfirmModal}
            onCancelClicked={handleCloseConfirmModal}
            onDoneClicked={submit}
          >
            {confirmationMessage}
          </ConfirmDialog>
          <div className="tw:flex tw:items-center tw:gap-3 tw:px-6 tw:py-3 tw:shrink-0">
            <button
              aria-label={Localization.translator.close}
              onClick={handlePreClose}
              className={`${ICON_BUTTON_CLASSES} tw:text-muted-foreground tw:hover:text-foreground`}
            >
              <CloseIcon size="sm" />
            </button>

            <div className="text small-header no-white-space-wrap tw:flex-1">
              {dialogCaption}
            </div>

            {isMultiMode && this.renderItemNavigation()}

            <Button
              className="bold"
              variant="default"
              onClick={preSubmit}
              disabled={!canFormsBeSubmitted}
            >
              {okActionCaption}
            </Button>
          </div>

          <div className="tw:flex-1 tw:overflow-y-auto tw:px-6">{children}</div>

          <div className="tw:shrink-0 tw:px-6 tw:pt-1">
            {this.renderFooter()}
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  public renderItemNavigation(): JSX.Element {
    const { goToPreviousItem, goToNextItem, navigationTitle } = this.props.store

    return (
      <div className="tw:absolute tw:left-[313px] tw:flex tw:items-center tw:justify-center">
        <div className="tw:flex tw:items-center tw:whitespace-nowrap">
          <button
            aria-label={Localization.translator.previous}
            onClick={goToPreviousItem}
            className={`${ICON_BUTTON_CLASSES} tw:text-primary`}
          >
            <ArrowBackIcon size="sm" />
          </button>
          <span className="tw:px-3">{navigationTitle}</span>
          <button
            aria-label={Localization.translator.next}
            onClick={goToNextItem}
            className={`${ICON_BUTTON_CLASSES} tw:text-primary`}
          >
            <ArrowForwardIcon size="sm" />
          </button>
        </div>
      </div>
    )
  }

  private renderLoader(): JSX.Element {
    return (
      this.props.isLoading && (
        <div className="full-height full-width absolute modal-overlay">
          <Loader />
        </div>
      )
    )
  }

  private renderFooter() {
    const { customFooterRenderer, errorInfo } = this.props
    return customFooterRenderer
      ? customFooterRenderer()
      : BaseAddEditDialogWrapper.renderErrorInfo(errorInfo)
  }
}
